
import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { useSettings } from "@/context/SettingsContext";
import { useIsMobile } from "@/hooks/use-mobile";
import RpgProgressPip from "./progress/RpgProgressPip";
import StandardProgressPip from "./progress/StandardProgressPip";
import ProgressCompletionButton from "./progress/ProgressCompletionButton";
import { 
  getResponsivePipSizes,
  getResponsiveRpgPipSizes,
  getResponsiveIconSizes,
  getResponsiveGapClasses,
  getButtonSizeClasses 
} from "./progress/progressPipUtils";
import { getDifficultyConfig, DifficultyRank } from "@/utils/difficultyRanks";

interface ProgressPipsProps {
  progress: number;
  maxProgress: number;
  onAddProgress?: () => Promise<boolean>;
  onCompleteProgress?: () => Promise<boolean>;
  onPipClick?: (pipValue: number) => Promise<boolean>;
  readonly?: boolean;
  size?: "sm" | "md" | "lg";
  difficultyRank?: DifficultyRank | null;
}

const ProgressPips: React.FC<ProgressPipsProps> = ({
  progress,
  maxProgress,
  onCompleteProgress,
  onPipClick,
  readonly = false,
  size = "md",
  difficultyRank
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [hoveredPip, setHoveredPip] = useState<number | null>(null);
  const { enableRpgMode, darkMode } = useSettings();
  const isMobile = useIsMobile();
  const isComplete = progress >= maxProgress;
  
  const difficultyConfig = getDifficultyConfig(difficultyRank);

  // Use responsive sizing based on pip count and mobile state
  const effectiveSize = isMobile && maxProgress > 7 ? "sm" : size;
  
  const pipSizeClass = enableRpgMode 
    ? getResponsiveRpgPipSizes(maxProgress, effectiveSize)
    : getResponsivePipSizes(maxProgress, effectiveSize);
    
  const iconSizeClass = getResponsiveIconSizes(maxProgress, effectiveSize);
  const gapClass = getResponsiveGapClasses(maxProgress, effectiveSize);
  const buttonSizeClass = getButtonSizeClasses()[effectiveSize];

  const handleComplete = async () => {
    if (isUpdating || !onCompleteProgress) return;
    
    setIsUpdating(true);
    try {
      await onCompleteProgress();
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePipClick = async (pipValue: number) => {
    if (isUpdating || !onPipClick) return;
    
    setIsUpdating(true);
    try {
      await onPipClick(pipValue);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Difficulty indicator */}
      {difficultyRank && (
        <div className="flex items-center gap-2 text-sm">
          <span className={cn(
            "font-medium",
            enableRpgMode ? difficultyConfig.rpgColor : difficultyConfig.color
          )}>
            {difficultyConfig.label}
          </span>
          <span className="text-muted-foreground">
            • {maxProgress} {enableRpgMode ? "milestones" : "steps"}
          </span>
        </div>
      )}
      
      <div className={cn(
        "flex items-center flex-wrap justify-start",
        gapClass
      )}>
        {Array.from({ length: maxProgress }).map((_, index) => {
          const pipValue = index + 1;
          const isActive = pipValue <= progress;
          const isHovered = hoveredPip === pipValue;
          
          const commonProps = {
            pipValue,
            isActive,
            isHovered,
            pipSizeClass,
            iconSizeClass,
            darkMode,
            readonly,
            onPipClick,
            onMouseEnter: () => !readonly && setHoveredPip(pipValue),
            onMouseLeave: () => !readonly && setHoveredPip(null),
            onClick: () => !readonly && onPipClick && handlePipClick(pipValue)
          };
          
          return (
            <div key={index}>
              {enableRpgMode ? (
                <RpgProgressPip
                  {...commonProps}
                  index={index}
                />
              ) : (
                <StandardProgressPip
                  {...commonProps}
                  size={effectiveSize}
                />
              )}
            </div>
          );
        })}
      </div>
      
      {!readonly && !isComplete && onCompleteProgress && (
        <ProgressCompletionButton
          isUpdating={isUpdating}
          enableRpgMode={enableRpgMode}
          size={effectiveSize}
          buttonSizeClass={buttonSizeClass}
          onComplete={handleComplete}
        />
      )}
    </div>
  );
};

export default ProgressPips;
